import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { identityApi } from '@/api/identity.api';
import { PortalDataList } from './PortalDataList';
import { Button } from '@/components/ui/Button';
import { notify } from '@/core/toast/notify';
import { showApiError } from '@/utils/apiError';

export function DeliveryPortalSections() {
  const { pathname } = useLocation();
  const section = pathname.split('/').pop() ?? 'dashboard';

  if (section === 'pod' || section === 'assigned') return <DeliveryAssignmentsSection confirmPod={section === 'pod'} />;
  if (section === 'route' || section === 'payments' || section === 'performance' || section === 'documents') {
    return <PortalDataList title={section.charAt(0).toUpperCase() + section.slice(1)} items={[]} emptyMessage="No items yet." />;
  }
  return <DeliveryAssignmentsSection />;
}

function DeliveryAssignmentsSection({ confirmPod = false }: { confirmPod?: boolean }) {
  const [items, setItems] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setItems(await identityApi.deliveryAssignments());
    } catch (err) {
      showApiError(err, 'Could not load deliveries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const confirm = async (id: number) => {
    try {
      await identityApi.confirmDeliveryPod(id);
      notify.success('Proof of delivery confirmed');
      void load();
    } catch (err) {
      showApiError(err, 'Could not confirm POD');
    }
  };

  return (
    <div className="space-y-4">
      <PortalDataList
        title={confirmPod ? 'Proof of delivery' : 'Assigned deliveries'}
        loading={loading}
        items={items.map((a) => ({
          id: Number(a.id),
          primary: String(a.dispatchCode ?? 'Delivery'),
          secondary: a.routeSummary ? String(a.routeSummary) : undefined,
          meta: String(a.podStatus ?? a.statusCode ?? ''),
        }))}
        emptyMessage="No deliveries assigned."
      />
      {confirmPod && items.filter((a) => a.podStatus === 'PENDING').map((a) => (
        <div key={String(a.id)} className="card p-4 flex justify-between items-center gap-4">
          <span className="font-medium">{String(a.dispatchCode)}</span>
          <Button size="sm" onClick={() => confirm(Number(a.id))}>Confirm POD</Button>
        </div>
      ))}
    </div>
  );
}
